// /src/utils/plantStore.js
import { create } from 'zustand'

const getApiUrl = (endpoint) => {
  // Use dynamic domain detection instead of hardcoded URL
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const baseUrl = isLocalhost ? 'http://localhost:8787' : window.location.origin
  return `${baseUrl}/api${endpoint}`;
};

const usePlantStore = create((set, get) => ({
  plants: [],
  isLoading: false,
  error: null,

  // Enhanced function to get plants with better persistence
  fetchPlants: async () => {
    set({ isLoading: true, error: null });
    try {
      // Try to get plants from API first
      const response = await fetch(getApiUrl('/plants-d1'));
      if (response.ok) {
        const data = await response.json();
        const apiPlants = data.plants || [];
        
        // Update state with API plants
        set({ plants: apiPlants, isLoading: false });
        console.log('📊 Plants loaded from API:', apiPlants.length);
        
        // Also update localStorage for offline support
        localStorage.setItem('localPlants', JSON.stringify(apiPlants));
      } else {
        console.log('⚠️ API not available, trying localStorage');
        // Fallback to localStorage
        const localPlants = JSON.parse(localStorage.getItem('localPlants') || '[]');
        set({ plants: localPlants, isLoading: false });
        console.log('📊 Plants loaded from localStorage:', localPlants.length);
      }
    } catch (error) {
      console.log('⚠️ API error, using localStorage only:', error.message);
      // Fallback to localStorage
      const localPlants = JSON.parse(localStorage.getItem('localPlants') || '[]');
      set({ plants: localPlants, isLoading: false });
    }
  },

  addPlant: async (plant) => {
    try {
      const formData = new FormData();
      formData.append('operation', 'add');
      formData.append('name', plant.name?.trim() || '');
      formData.append('scientificName', plant.scientificName?.trim() || '');
      formData.append('category', plant.category || 'tree');
      formData.append('description', plant.description || '');
      
      // Handle images
      if (plant.images && Array.isArray(plant.images)) {
        formData.append('images', JSON.stringify(plant.images));
      } else if (plant.image) {
        formData.append('imageUrl', plant.image);
      }
      
      formData.append('habitat', plant.habitat || '');
      formData.append('careLevel', plant.careLevel || 'Easy');

      // Add to localStorage immediately for better UX
      const newPlant = {
        id: Date.now() + Math.random(), // Generate unique ID
        name: plant.name?.trim() || '',
        scientificName: plant.scientificName?.trim() || '',
        category: plant.category || 'tree',
        description: plant.description || '',
        habitat: plant.habitat || '',
        careLevel: plant.careLevel || 'Easy',
        image: plant.image || (plant.images && plant.images[0]) || null,
        images: plant.images || [],
        createdAt: new Date().toISOString()
      };

      // Update state immediately
      set((state) => {
        const newPlants = [newPlant, ...state.plants];
        localStorage.setItem('localPlants', JSON.stringify(newPlants));
        return { plants: newPlants };
      });

      // Try to sync with API
      try {
        const response = await fetch(getApiUrl('/plants-d1'), {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            console.log('✅ Plant synced with API successfully');
            // Update the plant with the API response data if available
            set((state) => {
              const updatedPlants = state.plants.map(p => 
                p.id === newPlant.id ? { ...p, ...result.plant } : p
              );
              localStorage.setItem('localPlants', JSON.stringify(updatedPlants));
              return { plants: updatedPlants };
            });
          }
        } else {
          console.log('⚠️ API sync failed, but plant saved locally');
        }
      } catch (error) {
        console.log('⚠️ API sync error, but plant saved locally:', error.message);
      }
      
      return newPlant;
      
    } catch (error) {
      console.error('Error adding plant:', error);
      throw error;
    }
  },

  updatePlant: async (id, plant) => {
    try {
      // Update in localStorage immediately
      set((state) => {
        const updatedPlants = state.plants.map(p => 
          p.id === id ? { ...p, ...plant, updatedAt: new Date().toISOString() } : p
        );
        localStorage.setItem('localPlants', JSON.stringify(updatedPlants));
        return { plants: updatedPlants };
      });

      // Try to sync with API
      const formData = new FormData();
      formData.append('operation', 'update');
      formData.append('id', id);
      formData.append('name', plant.name?.trim() || '');
      formData.append('scientificName', plant.scientificName?.trim() || '');
      formData.append('category', plant.category || 'tree');
      formData.append('description', plant.description || '');
      
      if (plant.images && Array.isArray(plant.images)) {
        formData.append('images', JSON.stringify(plant.images));
      } else if (plant.image) {
        formData.append('imageUrl', plant.image);
      }
      
      formData.append('habitat', plant.habitat || '');
      formData.append('careLevel', plant.careLevel || 'Easy');

      try {
        const response = await fetch(getApiUrl('/plants-d1'), {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            console.log('✅ Plant update synced with API');
          }
        } else {
          console.log('⚠️ API update sync failed, but plant updated locally');
        }
      } catch (error) {
        console.log('⚠️ API update sync error, but plant updated locally:', error.message);
      }
      
      return plant;
      
    } catch (error) {
      console.error('Error updating plant:', error);
      throw error;
    }
  },

  deletePlant: async (id) => {
    try {
      // Remove from localStorage immediately
      set((state) => {
        const updatedPlants = state.plants.filter(p => p.id !== id);
        localStorage.setItem('localPlants', JSON.stringify(updatedPlants));
        return { plants: updatedPlants };
      });

      // Try to sync with API
      const formData = new FormData();
      formData.append('operation', 'delete');
      formData.append('id', id);

      try {
        const response = await fetch(getApiUrl('/plants-d1'), {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            console.log('✅ Plant deletion synced with API');
          }
        } else {
          console.log('⚠️ API deletion sync failed, but plant deleted locally');
        }
      } catch (error) {
        console.log('⚠️ API deletion sync error, but plant deleted locally:', error.message);
      }
      
    } catch (error) {
      console.error('Error deleting plant:', error);
      throw error;
    }
  },

  clearPlants: () => {
    localStorage.removeItem('localPlants');
    set({ plants: [] });
  }
}))

export default usePlantStore